import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { FileText, Eye, CheckCircle, XCircle, Clock, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const AdminApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedApp, setSelectedApp] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const fetchApplications = async () => {
      setLoading(true);
      try {
        let url = `${API}/admin/applications?page=${page}&limit=20`;
        if (statusFilter) url += `&status=${statusFilter}`;
        
        const res = await axios.get(url);
        setApplications(res.data.applications || []);
        setTotalPages(res.data.pages || 1);
      } catch (error) {
        console.error("Error fetching applications:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, [page, statusFilter]);

  const updateStatus = async (applicationId, newStatus) => {
    try {
      const res = await axios.put(`${API}/admin/applications/${applicationId}/status`, {
        status: newStatus
      });

      setApplications(prev => prev.map(a => a.application_id === applicationId ? res.data : a));
      if (selectedApp?.application_id === applicationId) {
        setSelectedApp(res.data);
      }
      toast.success(`Application ${newStatus}`);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />;
      case "rejected":
        return <XCircle weight="fill" className="w-4 h-4 text-red-400" />;
      case "submitted":
      case "under_review":
        return <Clock weight="fill" className="w-4 h-4 text-yellow-400" />;
      default:
        return <FileText weight="fill" className="w-4 h-4 text-zinc-400" />;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-applications-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Application Management</h1>
            <p className="text-zinc-400 mt-1">Review and manage grant applications</p>
          </div>
          
          <Select value={statusFilter || "all"} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-48 bg-zinc-900 border-zinc-800" data-testid="status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="under_review">Under Review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Card className="bg-zinc-950 border-zinc-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="loading-spinner" />
              </div>
            ) : applications.length === 0 ? (
              <div className="text-center py-16">
                <FileText weight="duotone" className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">No applications found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead>Grant</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {applications.map((app) => (
                    <TableRow key={app.application_id} className="border-zinc-800">
                      <TableCell className="font-medium">{app.grant_title}</TableCell>
                      <TableCell className="text-zinc-400">{app.user_id}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(app.status)}
                          <span className={`status-${app.status} px-2 py-0.5 rounded-sm text-xs font-mono uppercase`}>
                            {app.status.replace("_", " ")}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-zinc-400">
                        {app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => { setSelectedApp(app); setDialogOpen(true); }}
                            data-testid={`view-app-${app.application_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {app.status === "submitted" && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => updateStatus(app.application_id, "under_review")}
                                className="text-yellow-400 hover:text-yellow-300"
                                data-testid={`review-app-${app.application_id}`}
                              >
                                <Clock className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {(app.status === "submitted" || app.status === "under_review") && (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => updateStatus(app.application_id, "approved")}
                                className="text-emerald-400 hover:text-emerald-300"
                                data-testid={`approve-app-${app.application_id}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => updateStatus(app.application_id, "rejected")}
                                className="text-red-400 hover:text-red-300"
                                data-testid={`reject-app-${app.application_id}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button 
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              className="border-zinc-800"
            >
              Previous
            </Button>
            <span className="text-sm text-zinc-400 px-4">
              Page {page} of {totalPages}
            </span>
            <Button 
              variant="outline"
              size="sm"
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
              className="border-zinc-800"
            >
              Next
            </Button>
          </div>
        )}

        {/* View Application Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Application Details</DialogTitle>
            </DialogHeader>
            {selectedApp && (
              <div className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold">{selectedApp.grant_title}</h3>
                    <p className="text-sm text-zinc-400">User: {selectedApp.user_id}</p>
                  </div>
                  <span className={`status-${selectedApp.status} px-3 py-1 rounded-sm text-sm font-mono uppercase`}>
                    {selectedApp.status.replace("_", " ")}
                  </span>
                </div>

                <div className="text-sm text-zinc-400">
                  <p>Created: {new Date(selectedApp.created_at).toLocaleString()}</p>
                  {selectedApp.submitted_at && (
                    <p>Submitted: {new Date(selectedApp.submitted_at).toLocaleString()}</p>
                  )}
                </div>

                <div className="border border-zinc-800 rounded-sm p-4">
                  <h4 className="text-sm font-semibold text-zinc-400 mb-2">Application Content</h4>
                  <pre className="whitespace-pre-wrap text-sm text-zinc-300 font-mono bg-zinc-900 p-4 rounded-sm max-h-96 overflow-y-auto">
                    {selectedApp.draft_content || "No content"}
                  </pre>
                </div>

                {(selectedApp.status === "submitted" || selectedApp.status === "under_review") && (
                  <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
                    {selectedApp.status === "submitted" && (
                      <Button 
                        variant="outline"
                        onClick={() => updateStatus(selectedApp.application_id, "under_review")}
                        className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        Mark Under Review
                      </Button>
                    )}
                    <Button 
                      onClick={() => updateStatus(selectedApp.application_id, "approved")}
                      className="bg-emerald-500 text-black font-bold hover:bg-emerald-400"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => updateStatus(selectedApp.application_id, "rejected")}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminApplications;
